// Single-request detail. Header (status + recipient + merchant + totals),
// cart-items table, and (status-dependent) cancel action.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Loader2, MapPin, Phone, User as UserIcon, Tag, Store,
  X, CheckCircle2, Clock, Truck, CreditCard, Download, FileText, BadgeCheck, ChevronRight, Star,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill } from '../../components/shared/primitives';
import { DeliveryProof } from '../../components/shared/DeliveryProof';
import { DisputePanel } from '../../components/shared/DisputePanel';
import { PaymentPanel } from '../../components/shared/PaymentPanel';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import { requests, receipts, storefront, type ServiceRequestWithItems, type CertLang } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../../lib/money';
import { Button } from '../../components/stripe-ui/Button';
import { iconFor } from '../../lib/icons';
import { QuoteChanges } from '../../components/shared/QuoteChanges';
import { DelayNotice } from '../../components/shared/DelayNotice';

const CANCEL_OK = new Set([
  'draft', 'submitted', 'quote_pending', 'quoted',
  'customer_approval_pending', 'payment_pending',
]);

export function CustomerRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen — including this one.
  const nav = useCustomerNav(CUSTOMER_NAV);

  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['me', 'request', requestId],
    queryFn:  () => requests.get(requestId),
    enabled:  !isNaN(requestId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['me', 'request', requestId] });
    qc.invalidateQueries({ queryKey: ['me', 'requests'] });
  };

  const cancelMut = useMutation({
    mutationFn: () => requests.cancel(requestId),
    onSuccess: () => { toast.success('Request cancelled'); invalidate(); },
    onError: (e: Error) => toast.error(e.message || 'Cancel failed'),
  });

  const approveMut = useMutation({
    mutationFn: () => requests.approve(requestId),
    onSuccess: () => { toast.success('Quote approved — pay below to send it to the merchant'); invalidate(); },
    onError: (e: Error) => toast.error(e.message || 'Approve failed'),
  });

  const declineMut = useMutation({
    mutationFn: () => requests.decline(requestId),
    onSuccess: () => { toast.success('Quote declined'); invalidate(); },
    onError: (e: Error) => toast.error(e.message || 'Decline failed'),
  });

  const simulatePayMut = useMutation({
    mutationFn: () => requests.simulatePayment(requestId),
    onSuccess: () => { toast.success('Payment simulated — order sent to the merchant'); invalidate(); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  if (isNaN(requestId)) return (
    <PortalShell nav={nav} activeId="requests" title="Request not found">
      <Card>Bad request id.</Card>
    </PortalShell>
  );

  const r = q.data;
  const canCancel = r && CANCEL_OK.has(r.status);

  // The merchant on this order: assigned > preferred > the quoting merchant.
  const { user } = useAuth();
  const rolePrefix = user?.role === 'recipient' ? 'recipient' : 'customer';
  const merchantUserId = r?.fulfillment?.merchant_id ?? r?.preferred_merchant_id ?? r?.quotes?.[0]?.merchant?.user_id ?? null;
  const merchQ = useQuery({
    queryKey: ['storefront', 'merchant', merchantUserId],
    queryFn: () => storefront.merchant(merchantUserId!),
    enabled: !!merchantUserId,
  });
  const merch = merchQ.data?.merchant;
  return (
    <PortalShell
      nav={nav}
      activeId="requests"
      title={r ? `REQ-${String(r.id).padStart(4, '0')}` : 'Request'}
      subtitle={r ? `${r.category?.label ?? ''} for ${r.recipient?.full_name ?? ''}` : undefined}
      actions={
        canCancel ? (
          <Button
            tone="danger"
            onClick={() => { if (window.confirm('Cancel this request?')) cancelMut.mutate(); }}
            loading={cancelMut.isPending}
            disabled={cancelMut.isPending}
          >
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <Link to="/customer/requests" className="inline-flex items-center gap-1.5 text-[12px] text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]">
          <ArrowLeft className="w-3.5 h-3.5" /> All requests
        </Link>

        {q.isLoading && (
          <Card><div className="text-center py-12 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>
        )}

        {q.error && (
          <Card><p className="text-[13px] text-[var(--color-danger-fg)]">{(q.error as Error).message ?? 'Could not load request.'}</p></Card>
        )}

        {r && (
          <>
            {/* Header card */}
            <Card>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <StatusPill status={r.status} />
                  <h2 className="mt-2 text-[22px] font-semibold tracking-tight text-[var(--color-ink)]">
                    {r.title || `${r.category?.label ?? 'Request'} for ${r.recipient?.full_name ?? ''}`}
                  </h2>
                  {r.description && <p className="mt-1 text-[13px] text-[var(--color-secondary)] max-w-2xl">{r.description}</p>}
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Cart subtotal</p>
                  <p className="mt-1 text-[26px] font-mono font-semibold text-[var(--color-ink)]">{formatHtgMinor(r.cart_subtotal_htg_minor)}</p>
                  <p className="text-[12px] text-[var(--color-secondary-2)] font-mono">≈ {formatUsdFromHtgMinor(r.cart_subtotal_htg_minor)}</p>
                  <p className="text-[11px] text-[var(--color-muted)] mt-1">Merchant adds delivery + platform fee in the quote.</p>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                <Meta icon={UserIcon} label="Recipient" value={r.recipient?.full_name ?? '—'} />
                <Meta icon={MapPin}   label="City"      value={r.city?.name ?? '—'} />
                <Meta icon={Tag}      label="Category"  value={r.category?.label ?? '—'} />
                <Meta icon={Store}    label="Merchant"  value={merch?.business_name ?? (merchantUserId ? `Merchant #${merchantUserId}` : 'Open to all')} />
              </div>
            </Card>

            {/* Fulfilling merchant — name, KYB ID, link to full details */}
            {merchantUserId && (
              <Card padding="p-0">
                <Link to={`/${rolePrefix}/merchants/${merchantUserId}`} className="flex items-center gap-3 p-4 hover:bg-[var(--color-panel)] transition-colors">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white">
                    <Store className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-muted)]">Fulfilled by</p>
                    <p className="text-[14px] font-semibold text-[var(--color-ink)] truncate inline-flex items-center gap-1.5">
                      {merch?.business_name ?? `Merchant #${merchantUserId}`}
                      {merch?.status === 'approved' && <BadgeCheck className="w-4 h-4 text-[var(--color-success-fg)]" />}
                    </p>
                    <p className="text-[12px] text-[var(--color-secondary-2)] inline-flex items-center gap-2 flex-wrap">
                      {merch?.merchant_code && <span className="font-mono">ID {merch.merchant_code}</span>}
                      {merch?.city?.name && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {merch.city.name}</span>}
                      {merch?.rating && <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-[var(--color-warning-fg)]" /> {Number(merch.rating).toFixed(1)}</span>}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold text-brand-600 shrink-0">View details <ChevronRight className="w-4 h-4" /></span>
                </Link>
              </Card>
            )}

            {/* Items */}
            <Card padding="p-0">
              <div className="p-5 border-b border-[var(--color-hairline)]">
                <SectionHeader title="Cart items" subtitle={`${r.cart_item_count} item${r.cart_item_count === 1 ? '' : 's'}`} />
              </div>
              {(r.items?.length ?? 0) === 0 ? (
                <div className="text-center py-12 text-[13px] text-[var(--color-secondary-2)]">
                  {r.description ? 'Text-mode request — see description above.' : 'No items.'}
                </div>
              ) : (
                <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                  <thead className="text-[10.5px] uppercase tracking-wider text-[var(--color-muted)] bg-[var(--color-panel)]">
                    <tr>
                      <th className="text-left py-2.5 px-5 font-medium">Item</th>
                      <th className="text-right py-2.5 px-3 font-medium">Qty</th>
                      <th className="text-right py-2.5 px-3 font-medium">Unit (HTG)</th>
                      <th className="text-right py-2.5 px-5 font-medium">Line (HTG)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline)]">
                    {r.items!.map((it) => {
                      const Icon = iconFor(null);
                      return (
                        <tr key={it.id}>
                          <td className="py-2.5 px-5">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                                <Icon className="w-4 h-4" />
                              </span>
                              <div className="min-w-0">
                                <p className="font-semibold text-[var(--color-ink)]">{it.name_snapshot}</p>
                                {it.notes && <p className="text-[11px] text-[var(--color-secondary-2)]">{it.notes}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-[var(--color-ink)]">{parseFloat(it.quantity)}</td>
                          <td className="py-2.5 px-3 text-right font-mono text-[var(--color-secondary)]">{(it.unit_price_htg_minor / 100).toFixed(0)}</td>
                          <td className="py-2.5 px-5 text-right font-mono font-semibold text-[var(--color-ink)]">{(it.line_total_htg_minor / 100).toFixed(0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-[var(--color-panel)]">
                    <tr>
                      <td colSpan={3} className="py-2.5 px-5 text-right text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">Subtotal</td>
                      <td className="py-2.5 px-5 text-right font-mono font-semibold text-[var(--color-ink)]">{(r.cart_subtotal_htg_minor / 100).toFixed(0)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </Card>

            {/* Quote round-trip (Phase 2) */}
            <QuoteSection
              request={r}
              onApprove={() => approveMut.mutate()}
              onDecline={() => { if (window.confirm('Decline this quote? The request will be cancelled.')) declineMut.mutate(); }}
              approving={approveMut.isPending}
              declining={declineMut.isPending}
              onPaid={() => { toast.success('Paid — the merchant is preparing your order'); invalidate(); }}
            />

            {/* Fulfillment (post-payment) */}
            <FulfillmentSection
              request={r}
              onSimulate={() => simulatePayMut.mutate()}
              simulating={simulatePayMut.isPending}
            />

            {/* Verified-or-refunded: report a problem / dispute status */}
            <DisputePanel requestId={r.id} status={r.status} canOpen />

            {/* Recipient contact card */}
            {r.recipient && (
              <Card>
                <SectionHeader title="Recipient" />
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)] text-[12px] font-semibold">
                    {initialsOf(r.recipient.full_name)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--color-ink)]">{r.recipient.full_name}</p>
                    <p className="text-[12px] text-[var(--color-secondary-2)] inline-flex items-center gap-2">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city?.name ?? ''}</span>
                      {r.recipient && 'phone_e164' in r.recipient && (
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {(r.recipient as { phone_e164?: string }).phone_e164}</span>
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </PortalShell>
  );

  function Meta({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
    return (
      <div className="border border-[var(--color-hairline)] rounded-lg p-3">
        <p className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)] inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
        <p className="mt-1 text-[13px] font-semibold text-[var(--color-ink)] truncate">{value}</p>
      </div>
    );
  }
}

function initialsOf(n: string) {
  return n.split(' ').filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

function usd(minor: number) { return `$${(minor / 100).toFixed(2)}`; }

// ─── Quote round-trip (Phase 2) ──────────────────────────────

function QuoteSection({
  request, onApprove, onDecline, approving, declining, onPaid,
}: {
  request: ServiceRequestWithItems;
  onApprove: () => void;
  onDecline: () => void;
  approving: boolean;
  declining: boolean;
  /** Refetch once Stripe confirms: the server has advanced the request. */
  onPaid: () => void;
}) {
  const quote = request.quotes?.[0];

  // Awaiting the merchant.
  if (request.status === 'quote_pending' || request.status === 'submitted') {
    return (
      <Card padding="p-4" className="bg-[#EAF0FE]/50 border-[#DCE4F6]">
        <p className="text-[12.5px] text-[#063B9E] inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Sent to the merchant — waiting for their price. You'll get a quote in USD to review before paying.
        </p>
      </Card>
    );
  }

  if (!quote) return null;

  const expired = new Date(quote.expires_at).getTime() < Date.now();
  const awaiting = request.status === 'quoted' && quote.status === 'submitted';

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <SectionHeader title="Merchant quote" subtitle={quote.merchant?.business_name ? `from ${quote.merchant.business_name}` : undefined} />
        <StatusPill status={request.status === 'payment_pending' ? 'approved' : quote.status} />
      </div>

      {quote.items && quote.items.length > 0 && (
        <div className="border border-[var(--color-hairline)] rounded-lg divide-y divide-[var(--color-hairline)] mb-4">
          {quote.items.map((it) => (
            <div key={it.id} className="flex items-center justify-between px-3 py-2 text-[12.5px]">
              <span className="text-[var(--color-body)]">{it.description} <span className="text-[var(--color-muted)]">× {parseFloat(it.quantity)}</span></span>
              <span className="font-mono text-[var(--color-ink)]">{formatHtgMinor(it.line_htg_minor)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Above the totals on purpose: the customer should see what changed
          BEFORE the number they are being asked to approve. */}
      <QuoteChanges requestItems={request.items ?? []} quoteItems={quote.items ?? []} />

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Row label="Goods subtotal" value={formatHtgMinor(quote.subtotal_htg_minor)} />
          <Row label="Delivery" value={formatHtgMinor(quote.delivery_htg_minor)} />
          <Row label="Platform fee" value={usd(quote.platform_fee_usd_minor)} />
          <div className="border-t border-[var(--color-hairline)] pt-2 flex items-center justify-between">
            <span className="text-[12.5px] font-semibold text-[var(--color-body)]">You pay (USD)</span>
            <span className="text-[20px] font-mono font-semibold text-[var(--color-ink)]">{usd(quote.total_customer_usd_minor)}</span>
          </div>
        </div>
        <div className="space-y-2 text-[12px] text-[var(--color-secondary)]">
          {quote.est_fulfillment_hours != null && (
            <p className="inline-flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[var(--color-muted)]" /> Est. delivery ~{quote.est_fulfillment_hours}h after payment</p>
          )}
          <p className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--color-muted)]" /> {expired ? 'Quote expired' : `Valid until ${new Date(quote.expires_at).toLocaleString()}`}</p>
          {quote.merchant_notes && <p className="text-[var(--color-secondary)]">“{quote.merchant_notes}”</p>}
        </div>
      </div>

      {awaiting && !expired && (
        <div className="mt-5 flex items-center gap-2">
          {/* The handoff requires the CTA to state the amount: "Approve and pay
              $X", never a bare "Continue". Someone should never commit money
              without the number on the button they press. */}
          <Button onClick={onApprove} loading={approving} disabled={approving || declining} leadingIcon={<CheckCircle2 className="w-3.5 h-3.5" />}>
            Approve and pay {usd(quote.total_customer_usd_minor)}
          </Button>
          <Button tone="secondary" onClick={onDecline} loading={declining} disabled={approving || declining}>Decline</Button>
        </div>
      )}
      {awaiting && expired && (
        <p className="mt-4 text-[12px] text-[var(--color-warning-fg)] bg-[var(--color-warning-bg)] border border-[var(--color-warning-border)] rounded-md px-3 py-2">This quote has expired — the merchant needs to re-quote before you can approve.</p>
      )}
      {/* Approving moves the request to `payment_pending`, which is where the
          money is actually taken. This used to render a note saying card
          payment would arrive "in Phase 3", so the CTA above promised "Approve
          and pay $X" and then nothing was ever charged. */}
      {request.status === 'payment_pending' && (
        <div className="mt-4 border-t border-[var(--color-hairline)] pt-4">
          <p className="text-[12.5px] font-semibold text-[var(--color-ink)]">
            Pay {usd(quote.total_customer_usd_minor)} to start fulfillment
          </p>
          <p className="mt-0.5 mb-3 text-[12px] text-[var(--color-secondary)]">
            The merchant is paid only after {request.recipient?.full_name ?? 'your family'} confirms
            delivery with their code.
          </p>
          <PaymentPanel
            payableType="grocery"
            payableId={request.id}
            onPaid={onPaid}
          />
        </div>
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between text-[12.5px]"><span className="text-[var(--color-secondary-2)]">{label}</span><span className="font-mono text-[var(--color-ink)]">{value}</span></div>;
}

// ─── Fulfillment (post-payment) ──────────────────────────────

const FULFILL_STATES = ['paid', 'assigned_to_merchant', 'in_progress', 'ready_for_delivery', 'delivered', 'confirmed'];
const STEP_OF: Record<string, number> = { not_started: 0, preparing: 1, ready: 2, out_for_delivery: 3, delivered: 4, confirmed: 5 };
const STEPS = ['Paid', 'Preparing', 'Ready', 'Out for delivery', 'Delivered', 'Confirmed'];

function FulfillmentSection({
  request, onSimulate, simulating,
}: {
  request: ServiceRequestWithItems;
  onSimulate: () => void;
  simulating: boolean;
}) {
  const r = request;
  const f = r.fulfillment;

  // Any merchant-reported delay, shown wherever the customer is tracking.
  // Silence on a stalled order is what makes people anxious.
  const delayNotice = <DelayNotice requestId={r.id} />;

  // Real card payment now lives in the quote panel above (PaymentPanel). This
  // demo shortcut marks a request paid WITHOUT taking money, so it must never
  // reach production — it would hand out groceries for free.
  //
  // `import.meta.env` is compared inline, not behind a helper: Vite only
  // eliminates the dead branch when it can see the literal comparison, and a
  // wrapper function silently kept this in the bundle.
  if (r.status === 'payment_pending') {
    // Real card payment lives in the quote panel above (PaymentPanel). This
    // shortcut marks a request paid WITHOUT taking money, so it must never
    // reach production — it would hand out groceries for free.
    //
    // Gated as an inline `&&` inside JSX, matching Login.tsx: Vite only
    // eliminates the dead branch when the literal comparison is in the
    // expression it is pruning. An early `return null` on the same condition
    // left the markup and the endpoint name in the production bundle.
    return import.meta.env.VITE_DEMO_LOGINS === 'true' ? (
      <Card padding="p-4" className="bg-[var(--color-warning-bg)] border-[var(--color-warning-border)]">
        <p className="text-[13px] font-semibold text-[var(--color-warning-fg)] inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Demo shortcut</p>
        <p className="mt-1 text-[12px] text-[var(--color-warning-fg)]">Skips Stripe and marks this paid, to exercise the delivery flow. Local builds only.</p>
        <Button className="mt-3" tone="secondary" onClick={onSimulate} loading={simulating} disabled={simulating} leadingIcon={<CreditCard className="w-3.5 h-3.5" />}>Simulate payment (demo)</Button>
      </Card>
    ) : null;
  }

  if (!FULFILL_STATES.includes(r.status)) return null;

  const cur = f ? (STEP_OF[f.status] ?? 0) : (r.status === 'confirmed' ? 5 : r.status === 'delivered' ? 4 : 0);

  return (
    <Card>
      <SectionHeader title="Fulfillment" subtitle="Preparation and delivery progress." />

      {/* Directly under the header, above the step tracker: if the order is
          late, that is the first thing the customer needs — not something to
          scroll past the progress bar to find. */}
      {delayNotice}

      <div className="flex items-center gap-1 mt-1 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <span className={cn('inline-flex w-6 h-6 items-center justify-center rounded-full text-[10px] font-bold', i <= cur ? 'bg-[var(--color-success-fg)] text-white' : 'bg-[var(--color-panel)] text-[var(--color-muted)]')}>
              {i < cur ? '✓' : i + 1}
            </span>
            <span className={cn('ml-1 text-[11px]', i <= cur ? 'text-[var(--color-ink)] font-semibold' : 'text-[var(--color-muted)]')}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-5 h-px bg-[var(--color-border)] mx-1.5" />}
          </div>
        ))}
      </div>

      <div className="mt-4"><DeliveryProof requestId={r.id} /></div>

      {r.delivery_pin && r.status !== 'confirmed' && (
        <div className="mt-4 rounded-lg bg-brand-50 border border-brand-100 p-3">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Delivery code</p>
          <p className="mt-0.5 text-[20px] font-mono font-bold text-[var(--color-ink)] tracking-[0.3em]">{r.delivery_pin}</p>
          <p className="text-[11px] text-[var(--color-secondary-2)]">Share with {r.recipient?.full_name?.split(' ')[0] ?? 'your recipient'} — they enter it to confirm receipt.</p>
        </div>
      )}

      {r.status === 'delivered' && (
        <div className="mt-4 rounded-lg bg-[var(--color-warning-bg)] border border-[var(--color-warning-border)] p-3">
          <p className="text-[12px] font-semibold text-[var(--color-warning-fg)] inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Delivered — waiting for {r.recipient?.full_name?.split(' ')[0] ?? 'your recipient'} to confirm receipt.</p>
          <p className="text-[11px] text-[var(--color-warning-fg)] mt-1">Only the recipient confirms — in their VoyeLakay app or by replying to the WhatsApp with the code above (or they give it to the merchant at the door). You'll get the receipt the moment they do.</p>
        </div>
      )}

      {r.status === 'confirmed' && (
        <div className="mt-4 space-y-3">
          <p className="text-[12px] text-[var(--color-success-fg)] bg-[var(--color-success-bg)] border border-[var(--color-success-border)] rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Delivery confirmed{f?.recipient_confirmed_at ? ` on ${new Date(f.recipient_confirmed_at).toLocaleString()}` : ''}.
          </p>
          <ReceiptDownload requestId={r.id} />
        </div>
      )}
    </Card>
  );
}

function ReceiptDownload({ requestId }: { requestId: number }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await receipts.download(requestId, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <p className="text-[12.5px] font-semibold text-[var(--color-ink)] inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-[var(--color-secondary-2)]" /> Official delivery receipt</p>
        <p className="text-[11px] text-[var(--color-secondary-2)]">Emailed to everyone on confirmation — download a copy:</p>
      </div>
      <div className="flex items-center gap-2">
        <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} className="text-[12px] border border-[var(--color-border)] rounded-md px-2 py-1.5 bg-white" aria-label="Receipt language">
          <option value="en">English</option><option value="fr">Français</option><option value="ht">Kreyòl</option>
        </select>
        <Button size="sm" onClick={dl} loading={busy} disabled={busy} leadingIcon={<Download className="w-3.5 h-3.5" />}>Receipt PDF</Button>
      </div>
    </div>
  );
}

